// 일일 장부 저장소(D3c): 신규 키 dungeonhero.daily.v1만 쓴다(기존 키 불변). 시간은 주입된 now()로만 읽는다.
// 쓰기 규칙: 읽을 수 없거나(읽기 실패·더 새 버전·손상 원문을 아직 못 보존) 기존 기록을 확인하지 못했으면 쓰지 않는다 — 덮어써서 잃는 것보다 낫다.
import { MemoryStorage } from './save';
import { scopedStorage } from '../characters/scope';
import { applyDaily, freshDaily, markPaid, isFutureDaily, mergeDaily, parseDaily, rollDaily, serializeDaily, type DailyOp, type DailyResult, type DailyState } from '../shared/daily';

export const DAILY_KEY = 'dungeonhero.daily.v1';
export const DAILY_BACKUP_KEY = DAILY_KEY + '.bak';
export interface DailyStorage { getItem(key: string): string | null; setItem(key: string, value: string): void }

function defaultStorage(): DailyStorage {
  // 지금 캐릭터가 있으면 그 캐릭터 키로(src/characters/scope.ts), 없으면 원래 키
  try { if (typeof localStorage !== 'undefined') return scopedStorage(localStorage); } catch { /* 차단 → 메모리 */ }
  return new MemoryStorage();
}

export class DailyStore {
  private state: DailyState;
  /** 손상 원문을 .bak에 보존했는지(화면 안내용) */
  corrupted = false;
  /** 더 새 버전 기록을 봤다: 읽기만 하고 쓰지 않는다 */
  readOnly = false;
  /** 저장소를 읽지 못했다(일시 오류): 다시 읽을 수 있을 때까지 쓰지 않는다 */
  readFailed = false;
  /** 마지막 저장 시도가 실패해 메모리에만 있다(다음 refresh에서 읽어 합친 뒤 다시 쓴다, 화면 경고용) */
  unsaved = false;
  /** 손상 원문 보존이 아직 안 됐다: 보존될 때까지 쓰지 않는다 */
  private pendingCorrupt: string | null = null;
  private opSeq = 0;
  constructor(private readonly now: () => number, private readonly storage: DailyStorage = defaultStorage()) {
    this.state = freshDaily(this.now());
    this.load();
  }

  /** 쓰기를 막는 상태인가 */
  get blocked(): boolean { return this.readOnly || this.readFailed || this.pendingCorrupt !== null; }

  private load(): void {
    let raw: string | null;
    try { raw = this.storage.getItem(DAILY_KEY); } catch { this.readFailed = true; return; }
    this.readFailed = false;
    if (raw === null) return;
    const parsed = parseDaily(raw, this.now());
    if (parsed) { this.state = rollDaily(parsed, this.now()); return; }
    if (isFutureDaily(raw)) { this.readOnly = true; return; }
    this.corrupted = true;
    this.pendingCorrupt = raw;
    this.preserveCorrupt();
  }

  /** 손상 원문을 .bak에 쓰고 되읽어 확인한다. 확인되기 전에는 쓰기를 막는다 */
  private preserveCorrupt(): void {
    const raw = this.pendingCorrupt;
    if (raw === null) return;
    try {
      this.storage.setItem(DAILY_BACKUP_KEY, raw);
      if (this.storage.getItem(DAILY_BACKUP_KEY) === raw) this.pendingCorrupt = null;
    } catch { /* 다음 refresh에서 다시 */ }
  }

  /** 메모리 상태를 그대로(날짜 넘김·복제 없음). 틱마다 읽는 조회용(PR #273 Codex 5차 — view()는 시각이 바뀔 때마다 장부를 복제한다) */
  get peek(): DailyState { return this.state; }

  /** 지금 시각 기준으로 날짜를 넘긴 상태 */
  view(): DailyState {
    this.state = rollDaily(this.state, this.now());
    return this.state;
  }

  /** 저장본을 다시 읽어 합치고, 못 쓴 것이 있으면 합친 뒤 다시 쓴다(다른 탭 기록을 덮어쓰지 않게 읽기가 먼저) */
  refresh(): DailyState {
    if (this.pendingCorrupt !== null) this.preserveCorrupt();
    let raw: string | null = null;
    let readOk = true;
    try { raw = this.storage.getItem(DAILY_KEY); } catch { readOk = false; }
    this.readFailed = !readOk;
    if (readOk && raw !== null) {
      const other = parseDaily(raw, this.now());
      if (other) {
        const merged = mergeDaily(this.state, other);
        // 저장본에 없는 내용이 합쳐졌으면(다른 탭이 덮어쓴 경우) 다시 쓰기 대상으로 표시한다
        if (merged.rev !== other.rev || merged.log.length !== other.log.length || merged.day !== other.day) this.unsaved = true;
        this.state = merged;
      } else if (isFutureDaily(raw)) this.readOnly = true;
      else if (this.pendingCorrupt === null) {
        // 실행 중 다른 탭·중단된 쓰기로 저장본이 망가졌다: load()와 같이 원문을 보존하기 전에는 쓰지 않는다
        this.corrupted = true;
        this.pendingCorrupt = raw;
        this.preserveCorrupt();
      }
    }
    this.view();
    if (this.unsaved && !this.blocked) this.persist();
    return this.state;
  }

  private persist(): void {
    try { this.storage.setItem(DAILY_KEY, serializeDaily(this.state)); this.unsaved = false; } catch { this.unsaved = true; }
  }

  nextOpId(tag: string): string { return `${tag}-${this.now()}-${++this.opSeq}`; }

  /** 지급 확인 표시(dailyPay.settleDaily). 저장까지 됐으면 true — 못 썼으면 다음 정산이 다시 시도한다(받는 쪽이 같은 id를 거른다) */
  markPaid(id: string): boolean {
    this.refresh();
    if (this.blocked) return false;
    const next = markPaid(this.state, id);
    if (next === this.state) return this.state.owed.some((p) => p.id === id && p.paid);
    this.state = next;
    this.persist();
    return !this.unsaved;
  }

  apply(op: DailyOp, town: number, region6 = false): DailyResult {
    this.refresh();
    if (this.blocked) return { state: this.state, code: 'read_only' };
    const prev = this.state;
    const res = applyDaily(prev, op, this.now(), town, region6);
    this.state = res.state;
    if (res.code === 'ok') {
      this.persist();
      // 저장하지 못한 연산은 성공이 아니다: 되돌린다. 메모리에만 남은 시도 소진·지급 건이 재접속 뒤 사라지고 지갑에만 보상이 남는 것을 막는다(PR #273 Codex P2)
      if (this.unsaved) { this.state = prev; return { state: prev, code: 'save_failed' }; }
    }
    return res;
  }
}
